import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sermonsApi } from '@/lib/api';
import { Play, Calendar, User, Clock, Search, X } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SermonsPage = () => {
  const [sermons, setSermons] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadYears();
  }, []);

  useEffect(() => {
    loadMonths();
  }, [selectedYear]);

  // Debounced search
  const loadSermons = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (searchQuery.trim()) {
        // Use search endpoint if there's a query
        data = await sermonsApi.search(
          searchQuery,
          selectedYear === 'all' ? null : parseInt(selectedYear),
          selectedMonth === 'all' ? null : parseInt(selectedMonth)
        );
      } else {
        // Use regular endpoint
        data = await sermonsApi.getAll(
          selectedYear === 'all' ? null : parseInt(selectedYear),
          selectedMonth === 'all' ? null : parseInt(selectedMonth)
        );
      }
      // Sort by date, newest first
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setSermons(data);
    } catch (error) {
      console.error('Error loading sermons:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, searchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadSermons();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(debounceTimer);
  }, [loadSermons]);

  const loadYears = async () => {
    try {
      const yearsData = await sermonsApi.getYears();
      setYears(yearsData);
    } catch (error) {
      console.error('Error loading years:', error);
    }
  };

  const loadMonths = async () => {
    try {
      const monthsData = await sermonsApi.getMonths(
        selectedYear === 'all' ? null : parseInt(selectedYear)
      );
      setMonths(monthsData);
      // Reset month filter if current selection is not available
      if (selectedMonth !== 'all' && !monthsData.includes(parseInt(selectedMonth))) {
        setSelectedMonth('all');
      }
    } catch (error) {
      console.error('Error loading months:', error);
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedMonth('all'); // Reset month when year changes
  };

  const clearFilters = () => {
    setSelectedYear('all');
    setSelectedMonth('all');
    setSearchQuery('');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    return `${year}, ${month} ${day}`;
  };

  const getFilterDescription = () => {
    const parts = [];
    if (searchQuery.trim()) {
      parts.push(`matching "${searchQuery}"`);
    }
    if (selectedMonth !== 'all') {
      parts.push(`from ${MONTH_NAMES[parseInt(selectedMonth) - 1]}`);
    }
    if (selectedYear !== 'all') {
      parts.push(selectedYear);
    }
    
    if (parts.length === 0) {
      return `Showing all sermons (${sermons.length})`;
    }
    return `Showing ${sermons.length} sermon${sermons.length !== 1 ? 's' : ''} ${parts.join(' ')}`;
  };

  const hasActiveFilters = selectedYear !== 'all' || selectedMonth !== 'all' || searchQuery.trim();

  return (
    <Layout>
      <div className="bg-stone-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Sermons</h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Watch our weekly messages and grow in your faith journey
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search sermons by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
                data-testid="sermon-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Year Filter */}
            <Select value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger className="w-full md:w-40" data-testid="year-filter">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Month Filter */}
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-full md:w-40" data-testid="month-filter">
                <SelectValue placeholder="All Months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {months.map((month) => (
                  <SelectItem key={month} value={month.toString()}>
                    {MONTH_NAMES[month - 1]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="whitespace-nowrap">
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Filter Description */}
          <p className="text-sm text-stone-500 mt-3">{getFilterDescription()}</p>
        </div>

        {/* Sermons Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
          </div>
        ) : sermons.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sermons.map((sermon) => (
              <Link key={sermon.id} to={`/sermons/${sermon.id}`}>
                <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                  <div className="relative aspect-video bg-stone-200">
                    {sermon.thumbnail_url || sermon.thumbnail_data ? (
                      <img
                        src={sermon.thumbnail_url || sermon.thumbnail_data}
                        alt={sermon.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-stone-200">
                        <Play className="h-12 w-12 text-amber-700/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-amber-700 rounded-full p-4">
                        <Play className="h-8 w-8 text-white" fill="white" />
                      </div>
                    </div>
                    {sermon.duration && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {sermon.duration}
                      </span>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-stone-800 mb-2 line-clamp-2">{sermon.title}</h3>
                    <div className="flex items-center text-sm text-stone-500 mb-1">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(sermon.date)}
                    </div>
                    {sermon.speaker && (
                      <div className="flex items-center text-sm text-stone-500">
                        <User className="h-4 w-4 mr-2" />
                        {sermon.speaker}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Play className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-800 mb-2">No Sermons Found</h3>
              <p className="text-stone-600 mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Check back soon for our latest messages'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SermonsPage;
